import { useEffect, useState } from "react";
import { Plus, ShieldCheck, ArrowClockwise, Trash } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function AddUserModal({ open, onClose, onSaved }) {
    const [form, setForm] = useState({
        email: "",
        name: "",
        password: "",
        role: "user",
    });
    const [busy, setBusy] = useState(false);
    if (!open) return null;

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/admin/users", form);
            toast.success("User created — share the initial password securely");
            onSaved();
            onClose();
            setForm({ email: "", name: "", password: "", role: "user" });
        } catch (e2) {
            toast.error(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(15,23,45,0.35)", backdropFilter: "blur(6px)" }}
            onClick={onClose}
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="glass-strong w-full max-w-md p-8"
                data-testid="user-modal"
            >
                <h2
                    className="text-2xl font-extrabold mb-6"
                    style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                >
                    Add user
                </h2>
                <div className="space-y-3">
                    <label className="block">
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            Email
                        </span>
                        <input
                            data-testid="new-user-email"
                            value={form.email}
                            onChange={upd("email")}
                            type="email"
                            required
                            className="glass-input"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            Name
                        </span>
                        <input
                            data-testid="new-user-name"
                            value={form.name}
                            onChange={upd("name")}
                            required
                            className="glass-input"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            Initial password (min 8 chars)
                        </span>
                        <input
                            data-testid="new-user-password"
                            value={form.password}
                            onChange={upd("password")}
                            required
                            minLength={8}
                            className="glass-input mono"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                            Role
                        </span>
                        <select
                            data-testid="new-user-role"
                            value={form.role}
                            onChange={upd("role")}
                            className="glass-input"
                        >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                        </select>
                    </label>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button type="button" onClick={onClose} className="btn-ghost">
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={busy}
                        className="btn-glossy"
                        data-testid="save-user-button"
                    >
                        {busy ? "Creating…" : "Create user"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function UsersPage() {
    const { user: current } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users");
            setUsers(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const resetTotp = async (u) => {
        if (!confirm(`Force ${u.email} to re-enroll TOTP on next login?`)) return;
        try {
            await api.post(`/admin/users/${u.id}/reset-totp`);
            toast.success("TOTP reset — they'll re-enroll on next login");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const toggleActive = async (u) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
            toast.success(u.is_active ? "Disabled" : "Enabled");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const del = async (u) => {
        if (!confirm(`Delete ${u.email}?`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-4" data-testid="users-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1
                        className="text-3xl font-extrabold"
                        style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                    >
                        Users
                    </h1>
                    <p className="text-sm text-[color:var(--dm-text-2)] mt-1">
                        Only admins can manage users. Every user must complete TOTP setup on first login.
                    </p>
                </div>
                <button
                    onClick={() => setOpenModal(true)}
                    className="btn-glossy"
                    data-testid="add-user-button"
                >
                    <Plus size={16} weight="bold" /> Add user
                </button>
            </div>

            <div className="glass overflow-hidden">
                <div
                    className="table-row sticky top-0 z-10 bg-white/85 backdrop-blur-md text-[0.7rem] uppercase tracking-wider text-[color:var(--dm-text-2)] font-bold"
                    style={{ gridTemplateColumns: "2fr 1.4fr 0.7fr 0.9fr 1fr 1fr" }}
                >
                    <div>Email</div>
                    <div>Name</div>
                    <div>Role</div>
                    <div>2FA</div>
                    <div>Last login</div>
                    <div className="text-right">Actions</div>
                </div>
                <div className="max-h-[70vh] overflow-y-auto">
                {loading && <div className="p-6 text-sm">Loading…</div>}
                {!loading &&
                    users.map((u) => (
                        <div
                            key={u.id}
                            className="table-row"
                            data-testid={`user-row-${u.email}`}
                            style={{ gridTemplateColumns: "2fr 1.4fr 0.7fr 0.9fr 1fr 1fr" }}
                        >
                            <div>
                                <div className="mono text-sm">{u.email}</div>
                                {!u.is_active && (
                                    <span className="pill pill-danger mt-1">disabled</span>
                                )}
                            </div>
                            <div className="text-sm">{u.name}</div>
                            <div>
                                <span className={`pill ${u.role === "admin" ? "pill-brand" : "pill-neutral"}`}>
                                    {u.role}
                                </span>
                            </div>
                            <div>
                                {u.totp_enabled ? (
                                    <span className="pill pill-ok">enabled</span>
                                ) : (
                                    <span className="pill pill-warn">pending</span>
                                )}
                            </div>
                            <div className="text-xs text-[color:var(--dm-text-2)] mono">
                                {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                            </div>
                            <div className="text-right flex gap-1 justify-end">
                                <button
                                    onClick={() => resetTotp(u)}
                                    className="btn-ghost !py-1 !px-2 text-xs"
                                    title="Force 2FA reset"
                                    data-testid={`reset-totp-${u.email}`}
                                >
                                    <ShieldCheck size={14} weight="duotone" />
                                </button>
                                <button
                                    onClick={() => toggleActive(u)}
                                    className="btn-ghost !py-1 !px-2 text-xs"
                                    title={u.is_active ? "Disable" : "Enable"}
                                    disabled={u.id === current?.id}
                                    data-testid={`toggle-active-${u.email}`}
                                >
                                    {u.is_active ? "Disable" : "Enable"}
                                </button>
                                <button
                                    onClick={() => del(u)}
                                    className="btn-ghost btn-danger !py-1 !px-2 text-xs"
                                    disabled={u.id === current?.id}
                                    data-testid={`delete-user-${u.email}`}
                                >
                                    <Trash size={14} weight="duotone" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <AddUserModal open={openModal} onClose={() => setOpenModal(false)} onSaved={load} />
        </div>
    );
}
